import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Clock, Loader2, Shield, X } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import {
  DeclineDialog,
  HealthStage,
  useConsentState,
  useInvalidateConsentState,
  type ConsentState,
  type ConsentType,
} from '@/features/consent';

interface ConsentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CONSENT_TYPES: ConsentType[] = ['terms', 'privacy', 'health_data'];

/**
 * Einwilligungen einsehen und widerrufen.
 *
 * Frueher zeigte dieses Modal statischen Text ("Du hast bei der
 * Registrierung zugestimmt") - eine Behauptung, die fuer 223 von 223
 * Profilen nicht stimmte, weil der Registrierungspfad die Einwilligung nie
 * erfasst hat. Jetzt liest es denselben Stand wie das Gate
 * (get_consent_state, Migration 172) und zeigt Fassung und Datum.
 *
 * Der Widerruf laeuft ueber denselben DeclineDialog wie die Ablehnung im
 * Gate und damit ueber delete_own_account(). Der fruehere clientseitige
 * Loeschpfad hat das Profil nie getroffen (auf profiles gibt es keine
 * DELETE-Policy) und auth.users nie angefasst - das Konto blieb bestehen,
 * obwohl der Dialog "unwiderruflich geloescht" zusagte.
 */
export function ConsentManagementModal({ isOpen, onClose }: ConsentManagementModalProps) {
  const { t, i18n } = useTranslation(['consent', 'common']);
  const { data: state, isLoading, isError } = useConsentState();
  const invalidateConsentState = useInvalidateConsentState();

  const [withdrawFor, setWithdrawFor] = useState<ConsentType[] | null>(null);
  const [showHealthDecision, setShowHealthDecision] = useState(false);

  if (!isOpen) return null;

  if (withdrawFor && state) {
    return (
      <DeclineDialog
        consentTypes={withdrawFor}
        state={state}
        source="profile"
        onCancel={() => setWithdrawFor(null)}
      />
    );
  }

  if (showHealthDecision && state) {
    return (
      <HealthStage
        state={state}
        source="profile"
        onCompleted={() => {
          setShowHealthDecision(false);
          invalidateConsentState();
        }}
        onDecline={() => {
          setShowHealthDecision(false);
          setWithdrawFor(['health_data']);
        }}
        onDismiss={() => setShowHealthDecision(false)}
      />
    );
  }

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  return createPortal(
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal max-w-lg">
          <div className="bottom-sheet-header">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <h2>{t('consent:management.title')}</h2>
              </div>
              <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="bottom-sheet-content">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/50" />
              </div>
            )}

            {isError && (
              <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-[10px]">
                {t('consent:management.loadError')}
              </div>
            )}

            {state && (
              <>
                <p className="text-white/60 text-sm mb-4">{t('consent:management.intro')}</p>

                <div className="space-y-3">
                  {CONSENT_TYPES.map((type) => (
                    <ConsentRow
                      key={type}
                      type={type}
                      state={state}
                      formatDate={formatDate}
                      onDecide={() => setShowHealthDecision(true)}
                      onWithdraw={() => setWithdrawFor([type])}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
            >
              {t('common:actions.close')}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

interface ConsentRowProps {
  type: ConsentType;
  state: ConsentState;
  formatDate: (value: string) => string;
  onDecide: () => void;
  onWithdraw: () => void;
}

function ConsentRow({ type, state, formatDate, onDecide, onWithdraw }: ConsentRowProps) {
  const { t } = useTranslation(['consent']);

  const latest = state.latest[type];
  const currentVersion = state.versions[type];
  const isGranted = latest?.decision === 'granted';
  const isOutdated = isGranted && latest.version !== currentVersion;

  // Nur die Art.-9-Einwilligung ist widerrufbar, ohne dass zugleich die
  // Grundlage der Nutzung entfaellt - und auch sie nur um den Preis der
  // Kontoloeschung. Fuer AGB und Datenschutz fuehrt derselbe Weg ueber das
  // Gate, dort steht der Ablehnen-Button.
  const canWithdraw = type === 'health_data' && isGranted;
  const canDecide = type === 'health_data' && !isGranted;

  let statusText: string;
  let statusIcon = <AlertTriangle className="w-4 h-4 text-yellow-400" />;

  if (isGranted) {
    statusText = t('consent:management.status.granted', { date: formatDate(latest.decided_at) });
    statusIcon = <Check className="w-4 h-4 text-green-400" />;
  } else if (latest?.decision === 'withdrawn') {
    statusText = t('consent:management.status.withdrawn', { date: formatDate(latest.decided_at) });
  } else if (latest?.decision === 'declined') {
    statusText = t('consent:management.status.declined', { date: formatDate(latest.decided_at) });
  } else if (latest?.decision === 'deferred' && latest.deferred_until) {
    statusText = t('consent:management.status.deferred', {
      date: formatDate(latest.deferred_until),
    });
    statusIcon = <Clock className="w-4 h-4 text-white/50" />;
  } else {
    statusText = t('consent:management.status.missing');
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex-shrink-0">{statusIcon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-white/90 font-medium text-sm">
            {t(`consent:management.types.${type}`)}
          </p>
          <p className="text-white/60 text-sm mt-0.5">{statusText}</p>

          {isGranted && !isOutdated && (
            <p className="text-white/40 text-xs mt-0.5">
              {t('consent:management.status.grantedVersion', { version: latest.version })}
            </p>
          )}

          {isOutdated && (
            <p className="text-yellow-300/80 text-xs mt-1">
              {t('consent:management.status.outdated', {
                version: latest.version,
                current: currentVersion,
              })}
            </p>
          )}

          {!isGranted && !canDecide && (
            <p className="text-white/40 text-xs mt-1">{t('consent:management.pendingHint')}</p>
          )}

          {canDecide && (
            <button
              onClick={onDecide}
              className="mt-3 w-full py-2 rounded-lg bg-pp-orange/20 border border-pp-orange/30 text-pp-orange text-sm font-medium hover:bg-pp-orange/30"
            >
              {t('consent:management.grantNow')}
            </button>
          )}

          {canWithdraw && (
            <>
              <button
                onClick={onWithdraw}
                className="mt-3 w-full py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm font-medium hover:bg-red-500/30"
              >
                {t('consent:management.withdrawButton')}
              </button>
              <p className="text-white/40 text-xs mt-2">{t('consent:management.withdrawHint')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
